'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Ref, useState } from 'react';
import AnimatedSearchButton from '@/components/AnimatedSearchButton';
import IconClose from '@/components/assets/icon_close.svg';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
type SearchInputFieldProps = {
  /** Controlled input value. */
  value: string;
  placeholder: string;
  /** Drives the search icon's running animation while a search navigation is in flight. */
  isAnimating: boolean;
  onValueChange: (value: string) => void;
  onSearch: () => void;
  onClear: () => void;
  autoFocus?: boolean;
  className?: string;
  ref?: Ref<HTMLInputElement>;
};

/**
 * Shared rounded search field used by the ViverseApp top nav and the mobile search page.
 *
 * Owns only presentational focus state; the value, search and clear behavior stay with the caller.
 */
export default function SearchInputField(t0) {
  const $ = _c(33);
  const {
    value,
    placeholder,
    isAnimating,
    onValueChange,
    onSearch,
    onClear,
    autoFocus,
    className,
    ref
  } = t0;
  const t = useTranslations();
  const [isFocused, setIsFocused] = useState(false);
  let t1;
  if ($[0] !== className) {
    t1 = cn("group relative flex flex-1 items-center rounded-full bg-[#AAADB933] pl-4 transition-shadow duration-500", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isAnimating || isFocused;
  let t3;
  if ($[2] !== isAnimating || $[3] !== onSearch || $[4] !== t2) {
    t3 = <AnimatedSearchButton onClick={onSearch} isAnimating={isAnimating} isFocused={t2} />;
    $[2] = isAnimating;
    $[3] = onSearch;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = value ? "pr-12" : "pr-4";
  let t5;
  if ($[6] !== t4) {
    t5 = cn("selection:bg-vive-brand-50/30 selection:text-vive-brand-50 flex-1 border-none bg-transparent pl-3 tracking-[0.2px] shadow-none placeholder:text-[#AAADB9] focus-visible:ring-0", t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onValueChange) {
    t6 = e => onValueChange(e.target.value);
    $[8] = onValueChange;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsFocused(true);
    t8 = () => setIsFocused(false);
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  let t9;
  if ($[12] !== onSearch) {
    t9 = e_0 => {
      if (e_0.key === "Enter") {
        onSearch();
      }
    };
    $[12] = onSearch;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== autoFocus || $[15] !== placeholder || $[16] !== ref || $[17] !== t5 || $[18] !== t6 || $[19] !== t9 || $[20] !== value) {
    t10 = <Input ref={ref} value={value} placeholder={placeholder} className={t5} onChange={t6} onFocus={t7} onBlur={t8} onKeyDown={t9} autoFocus={autoFocus} />;
    $[14] = autoFocus;
    $[15] = placeholder;
    $[16] = ref;
    $[17] = t5;
    $[18] = t6;
    $[19] = t9;
    $[20] = value;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== onClear || $[23] !== t || $[24] !== value) {
    t11 = value ? <motion.div initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.8
    }} transition={{
      duration: 0.2
    }} className="absolute top-1/2 right-3 -translate-y-1/2"><button type="button" aria-label={t("worlds-landing.search.clear")} className="flex size-6 cursor-pointer items-center justify-center rounded-full border-2 border-white transition-opacity hover:opacity-70" onClick={onClear}><IconClose className="size-4 fill-white stroke-white" /></button></motion.div> : null;
    $[22] = onClear;
    $[23] = t;
    $[24] = value;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = <AnimatePresence>{t11}</AnimatePresence>;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t1 || $[29] !== t10 || $[30] !== t12 || $[31] !== t3) {
    t13 = <div className={t1}>{t3}{t10}{t12}</div>;
    $[28] = t1;
    $[29] = t10;
    $[30] = t12;
    $[31] = t3;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
}